import {t, useLanguage, interfaceLocale} from '../i18n';
import './BenefitsCompact.css';
import BenefitsLoading from "../components/BenefitsLoading";
// src/pages/MyMembershipsPage.jsx
// LOOP D2.4.1 — Client memberships mobile responsive polish
// LOOP D2.4 — Client-facing memberships premium revamp
// Premium cards + membership detail modal. Keeps getMyMemberships data flow.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { callCallableNativeSafe } from "../utils/callableNativeSafe";
import {
  ArrowLeft,
  RefreshCw,
  Crown,
  Store,
  CalendarDays,
  Ticket,
  Sparkles,
  ChevronRight,
  Clock,
  X,
  BadgeCheck,
  CreditCard,
  ShieldCheck,
  Layers,
} from "lucide-react";

const LOOP_MEMBERSHIPS_CACHE_KEY = "loop_my_memberships_cache_v1";
const LOOP_BENEFITS_CACHE_TTL_MS = 1000 * 60 * 10;

function readMembershipsCache() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const raw = window.localStorage.getItem(LOOP_MEMBERSHIPS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const savedAt = Number(parsed?.savedAt || 0);
    const items = Array.isArray(parsed?.items) ? parsed.items : [];
    if (!savedAt || Date.now() - savedAt > LOOP_BENEFITS_CACHE_TTL_MS) return items.length ? { items, stale: true } : null;
    return { items, stale: false };
  } catch (_) {
    return null;
  }
}

function writeMembershipsCache(items = []) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.setItem(LOOP_MEMBERSHIPS_CACHE_KEY, JSON.stringify({ items: Array.isArray(items) ? items : [], savedAt: Date.now() }));
  } catch (_) {}
}

function money(value, currency = "USD") {
  const n = Number(value || 0);
  return new Intl.NumberFormat(interfaceLocale(),{style:"currency",currency}).format(Number.isFinite(n)?n:0);
}

function toDate(value) {
  try {
    if (!value) return null;
    const d = value.seconds ? new Date(value.seconds * 1000) : value.toDate ? value.toDate() : new Date(value);
    return d && !Number.isNaN(d.getTime()) ? d : null;
  } catch (_) {
    return null;
  }
}

function fmtDate(value) {
  const d = toDate(value);
  if (!d) return t("Sin fecha");
  return d.toLocaleDateString(interfaceLocale(), { day: "2-digit", month: "short", year: "numeric" });
}

function daysUntil(value) {
  const d = toDate(value);
  if (!d) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date(d);
  end.setHours(0, 0, 0, 0);
  return Math.ceil((end.getTime() - today.getTime()) / 86400000);
}

function statusValue(value) {
  const s = String(value || "active").toLowerCase();
  if (["cancelled", "canceled", "cancelada", "cancelado"].includes(s)) return "cancelled";
  if (["expired", "expirada", "expirado", "vencida", "vencido"].includes(s)) return "expired";
  if (["paused", "pausada", "pausado"].includes(s)) return "paused";
  return "active";
}

function statusLabel(value) {
  const s = statusValue(value);
  if (s === "cancelled") return t("Cancelada");
  if (s === "expired") return t("Vencida");
  if (s === "paused") return t("Pausada");
  return t("Activa");
}

function cycleLabel(value) {
  const s = String(value || "monthly").toLowerCase();
  if (s === "weekly") return t("semana");
  if (s === "quarterly") return t("trimestre");
  if (s === "yearly") return t("año");
  return t("mes");
}

function cycleText(value) {
  const s = String(value || "monthly").toLowerCase();
  if (s === "weekly") return t("Semanal");
  if (s === "quarterly") return t("Trimestral");
  if (s === "yearly") return t("Anual");
  return t("Mensual");
}

function creditUnitLabel(value, count = 2) {
  const raw = String(value || "créditos").trim();
  if (!raw) return t(count === 1 ? "crédito" : "créditos");
  if (raw.toLowerCase() === "servicios") return t(count === 1 ? "servicio" : "servicios");
  if (raw.toLowerCase() === "servicio") return t(count === 1 ? "servicio" : "servicios");
  if (raw.toLowerCase() === "credito" || raw.toLowerCase() === "crédito") return t(count === 1 ? "crédito" : "créditos");
  return raw;
}

function membershipBenefitType(membership = {}, plan = {}) {
  const raw = String(
    membership.benefitType ||
    membership.membershipType ||
    membership.membershipBenefitType ||
    membership.usageLimitType ||
    plan.benefitType ||
    plan.membershipType ||
    plan.usageLimitType ||
    ""
  ).trim().toLowerCase();

  if (membership.unlimitedUsage === true || membership.isUnlimited === true || plan.unlimitedUsage === true) return "unlimited";
  if (["unlimited", "ilimitado", "ilimitada", "uso_ilimitado", "uso ilimitado", "unlimited_usage"].includes(raw)) return "unlimited";
  if (["discount", "descuento", "discount_only", "solo_descuento", "solo descuento"].includes(raw)) return "discount";

  const included = Number(membership.includedCredits ?? plan.includedCredits ?? membership.creditsTotal ?? plan.credits ?? 0);
  const discount = Number(plan.discountPercent ?? membership.discountPercent ?? 0);
  if ((!Number.isFinite(included) || included <= 0) && Number.isFinite(discount) && discount > 0) return "discount";
  return "credits";
}

function membershipBenefitName(membership = {}, plan = {}) {
  const type = membershipBenefitType(membership, plan);
  if (type === "unlimited") return t("Uso ilimitado");
  if (type === "discount") return t("Solo descuento");
  return t("Créditos incluidos");
}

function businessRouteParam(row = {}) {
  const business = row?.business || {};
  return String(
    business.slug ||
    business.publicSlug ||
    business.businessSlug ||
    business.slugLower ||
    business.routeParam ||
    business.id ||
    row.businessId ||
    ""
  ).trim();
}

function buildBookingPath(row = {}) {
  const routeParam = businessRouteParam(row);
  if (!routeParam) return "/";
  const membership = row?.membership || {};
  const plan = row?.plan || {};
  const qs = new URLSearchParams();
  qs.set("useMembership", "1");
  if (membership.id) qs.set("membershipId", membership.id);
  if (plan.id || membership.planId) qs.set("planId", plan.id || membership.planId);
  return `/business/${encodeURIComponent(routeParam)}?${qs.toString()}`;
}

function membershipCreditStats(membership = {}, plan = {}) {
  const benefitType = membershipBenefitType(membership, plan);
  const isUnlimited = benefitType === "unlimited";
  const isDiscountOnly = benefitType === "discount";
  const includedRaw = Number(membership.includedCredits ?? plan.includedCredits ?? membership.creditsTotal ?? plan.credits ?? 0);
  const remainingRaw = Number(membership.creditsRemaining ?? membership.remainingCredits ?? includedRaw);
  const usedRaw = Number(membership.creditsUsed ?? membership.usedCredits ?? NaN);

  if (isUnlimited || isDiscountOnly) {
    return {
      benefitType,
      isUnlimited,
      isDiscountOnly,
      isCreditBased: false,
      included: 0,
      remaining: 0,
      used: 0,
      usedPct: 0,
      remainingPct: 100,
    };
  }

  const included = Number.isFinite(includedRaw) ? Math.max(0, includedRaw) : 0;
  const remaining = Number.isFinite(remainingRaw) ? Math.max(0, Math.min(remainingRaw, included || remainingRaw)) : 0;
  const used = Number.isFinite(usedRaw) ? Math.max(0, usedRaw) : Math.max(0, included - remaining);
  const usedPct = included > 0 ? Math.max(0, Math.min(100, Math.round((used / included) * 100))) : 0;
  const remainingPct = included > 0 ? Math.max(0, Math.min(100, Math.round((remaining / included) * 100))) : 0;
  return { benefitType, isUnlimited, isDiscountOnly, isCreditBased: true, included, remaining, used, usedPct, remainingPct };
}

function activityDate(value) {
  try {
    if (!value) return "";
    const d = value.seconds ? new Date(value.seconds * 1000) : value.toDate ? value.toDate() : new Date(value);
    if (!d || Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(interfaceLocale(), { day: "2-digit", month: "short" });
  } catch (_) {
    return "";
  }
}

function activityLine(item = {}) {
  const type = String(item.activityType || item.type || "").toLowerCase();
  if (item.title) return item.title;
  if (type === "credit_used") return `${item.serviceName || "Servicio"} · crédito usado`;
  if (type === "credit_adjusted") return Number(item.amount || item.deltaCredits || 0) >= 0 ? "Crédito devuelto" : "Crédito ajustado";
  if (type === "membership_renewed") return "Membresía renovada";
  if (type === "membership_expired") return "Membresía vencida";
  if (type === "membership_granted") return "Membresía asignada";
  return "Actividad de membresía";
}

function RenewalHint({ membership, plan, subscription }) {
  const endDate =
    subscription?.currentPeriodEnd ||
    subscription?.nextBillingAt ||
    membership.endDate ||
    membership.expiresAt ||
    membership.currentPeriodEnd ||
    membership.nextRenewalAt;
  const left = daysUntil(endDate);
  const cancellationScheduled =
    subscription?.cancelAtPeriodEnd === true ||
    String(subscription?.status || "").toLowerCase() === "cancel_at_period_end";
  const autoRenew =
    Boolean(subscription?.id || membership?.sourceSubscriptionId) ||
    membership.autoRenew === true ||
    plan.autoRenew === true;

  if (left == null) {
    return <span><Clock size={14} />{" " + t("Vigencia sin fecha configurada")}</span>;
  }
  if (left < 0) return <span><Clock size={14} />{" " + t("Venció hace") + " "}{Math.abs(left)}{" " + t("día")}{Math.abs(left) === 1 ? "" : "s"}</span>;
  if (left === 0) {
    return <span><Clock size={14} /> {cancellationScheduled ? t("Termina hoy · no se renovará") : autoRenew ? t("Renueva hoy") : t("Vence hoy")}</span>;
  }
  if (cancellationScheduled) {
    return <span><Clock size={14} />{" " + t("No se renovará · termina en") + " "}{left}{" " + t("día")}{left === 1 ? "" : "s"}</span>;
  }
  return <span><Clock size={14} /> {autoRenew ? t("Renueva") : t("Vence")}{" " + t("en") + " "}{left}{" " + t("día")}{left === 1 ? "" : "s"}</span>;
}

function planTitle(row = {}) {
  return row?.plan?.name || row?.membership?.planName || "Membresía";
}

function planDescription(row = {}) {
  return row?.plan?.description || row?.membership?.description || "Tus beneficios activos para próximas reservas.";
}

function subscriptionStatus(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (["cancel_at_period_end", "cancel-at-period-end"].includes(raw)) return "cancel_at_period_end";
  if (["cancelled", "canceled"].includes(raw)) return "cancelled";
  if (raw === "expired") return "expired";
  if (raw === "past_due") return "past_due";
  if (raw === "suspended") return "suspended";
  if (raw === "draft" || raw === "pending_payment" || raw === "pending_activation") return raw;
  return raw || "active";
}

function subscriptionIdFromRow(row = {}, subscription = null) {
  const membership = row?.membership || {};
  return String(
    subscription?.id ||
    membership.sourceSubscriptionId ||
    membership.subscriptionId ||
    membership.membershipSubscriptionId ||
    ""
  ).trim();
}

function subscriptionForRow(row = {}, subscriptions = []) {
  const membership = row?.membership || {};
  const businessId = String(row?.businessId || membership.businessId || "").trim();
  const planId = String(row?.plan?.id || membership.planId || "").trim();
  const exactId = subscriptionIdFromRow(row);

  if (exactId) {
    const exact = subscriptions.find((item) => String(item?.id || "").trim() === exactId);
    if (exact) return exact;
  }

  return subscriptions.find((item) => (
    String(item?.businessId || "").trim() === businessId &&
    String(item?.planId || "").trim() === planId
  )) || null;
}

function subscriptionCancellationScheduled(subscription = null) {
  return Boolean(
    subscription?.cancelAtPeriodEnd === true ||
    subscriptionStatus(subscription?.status) === "cancel_at_period_end"
  );
}

function subscriptionPeriodEnd(row = {}, subscription = null) {
  const membership = row?.membership || {};
  return (
    subscription?.currentPeriodEnd ||
    subscription?.nextBillingAt ||
    membership.endDate ||
    membership.expiresAt ||
    membership.currentPeriodEnd ||
    membership.nextRenewalAt ||
    null
  );
}

function subscriptionPaymentLabel(subscription = null) {
  const payment = subscription?.payment || {};
  const brand = String(
    payment.cardBrand ||
    payment.brand ||
    subscription?.cardBrand ||
    ""
  ).trim();
  const lastFour = String(
    payment.cardLastFour ||
    payment.lastFour ||
    payment.last4 ||
    subscription?.cardLastFour ||
    ""
  ).replace(/\D/g, "").slice(-4);

  if (!lastFour) return t("Método guardado para renovación");
  return `${brand ? `${brand} ` : ""}•••• ${lastFour}`;
}

function subscriptionStatusLabel(subscription = null) {
  const status = subscriptionStatus(subscription?.status);
  if (status === "cancel_at_period_end") return t("Activa hasta fin de ciclo");
  if (status === "cancelled") return t("Cancelada");
  if (status === "expired") return t("Vencida");
  if (status === "past_due") return "Pago pendiente";
  if (status === "suspended") return t("Suspendida");
  if (["draft", "pending_payment", "pending_activation"].includes(status)) return t("Pendiente");
  return t("Activa");
}

function membershipCardStatusLabel(membership = {}, subscription = null) {
  if (subscriptionCancellationScheduled(subscription)) return t("No renueva");
  return statusLabel(membership.status);
}

function canManageRecurringSubscription(row = {}, subscription = null) {
  const membership = row?.membership || {};
  const acquisition = String(membership.acquisitionType || subscription?.acquisitionType || "").trim().toLowerCase();
  return Boolean(
    subscriptionIdFromRow(row, subscription) &&
    (acquisition === "online_subscription" || subscription)
  );
}

function callableMessage(error, fallback) {
  const raw = String(
    error?.details?.message ||
    error?.message ||
    error?.code ||
    error ||
    ""
  ).trim();
  return raw || fallback;
}

function MembershipBadge({ large = false }) {
  return <div className={`mmp-badge ${large ? "is-large" : ""}`}><Crown size={large ? 28 : 20} /></div>;
}

function InfoTile({ icon: Icon, label, value, hint }) {
  return (
    <div className="mmp-info-tile">
      {Icon ? <span className="mmp-info-icon"><Icon size={16} /></span> : null}
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        {hint ? <small>{hint}</small> : null}
      </div>
    </div>
  );
}

function MembershipDetailModal({ row, subscription, onClose, onManage }) {
  useLanguage();
  const navigate = useNavigate();
  const plan = row?.plan || {};
  const membership = row?.membership || {};
  const business = row?.business || {};
  const status = statusValue(membership.status);
  const active = status === "active";
  const credits = membershipCreditStats(membership, plan);
  const unitRemaining = creditUnitLabel(membership.creditUnit || plan.creditUnit, credits.remaining);
  const discount = Number(plan.discountPercent ?? membership.discountPercent ?? 0) || 0;
  const price = plan.price ?? membership.price;
  const bookingPath = buildBookingPath(row);
  const activity = Array.isArray(row?.activityRecent) ? row.activityRecent.slice(0, 5) : [];
  const start = membership.startDate || membership.activatedAt || membership.currentPeriodStart;
  const end = subscriptionPeriodEnd(row, subscription);
  const manageable = canManageRecurringSubscription(row, subscription);

  const modal = (
    <div className="mmp-modal-layer" role="dialog" aria-modal="true" aria-label={t("Detalle de membresía")}>
      <div className="mmp-modal-backdrop" onClick={onClose} />
      <section className="mmp-modal-card">
        <button type="button" className="mmp-modal-close" onClick={onClose} aria-label={t("Cerrar")}><X size={20} /></button>

        <div className="mmp-detail-head">
          <MembershipBadge large />
          <div className="mmp-detail-title">
            <span>{t("Detalle del beneficio")}</span>
            <h2>{planTitle(row)}</h2>
            <p><Store size={14} /> {business.name || membership.businessName || t("Negocio")}</p>
          </div>
          <em className={`mmp-status status-${status} ${subscriptionCancellationScheduled(subscription) ? "is-ending" : ""}`}>{membershipCardStatusLabel(membership, subscription)}</em>
        </div>

        <p className="mmp-detail-description">{planDescription(row)}</p>

        <section className="mmp-detail-section">
          <div className="mmp-section-heading">
            <h3>{credits.isUnlimited || credits.isDiscountOnly ? "Tu beneficio" : "Uso del ciclo"}</h3>
            <span>{cycleText(plan.billingCycle || membership.billingCycle)}</span>
          </div>

          {credits.isUnlimited ? (
            <div className="mmp-unlimited-box">
              <span><Sparkles size={18} />{t("Uso ilimitado")}</span>
              <strong>{t("Reserva este beneficio todas las veces que quieras mientras la membresía esté activa.")}</strong>
              <p>{t("No se descuentan créditos. Solo se valida que el servicio esté incluido y que tu plan esté vigente.")}</p>
            </div>
          ) : credits.isDiscountOnly ? (
            <div className="mmp-unlimited-box">
              <span><BadgeCheck size={18} />{" " + t("Descuento activo")}</span>
              <strong>{discount > 0 ? t('{discount}% de descuento',{discount}) : "Descuento configurado por el negocio"}</strong>
              <p>{t("Este plan aplica descuento sin manejar créditos disponibles.")}</p>
            </div>
          ) : (
            <>
              <div className="mmp-detail-usage-grid">
                <InfoTile icon={Ticket} label={t("Disponibles")} value={`${credits.remaining} ${unitRemaining}`} hint="Beneficios restantes" />
                <InfoTile icon={BadgeCheck} label={t("Usados")} value={`${credits.used} de ${credits.included || credits.remaining}`} hint="Servicios utilizados" />
              </div>
              <div className="mmp-progress is-detail" aria-label={t("{percent}% del ciclo utilizado", {percent: credits.usedPct})}>
                <i style={{ width: `${credits.usedPct}%` }} />
              </div>
              <p className="mmp-progress-note">{credits.usedPct}{t("% del ciclo utilizado")}</p>
            </>
          )}
        </section>

        <section className="mmp-detail-grid">
          <InfoTile icon={Layers} label={t("Tipo")} value={membershipBenefitName(membership, plan)} hint={plan.eligibleMode === "selected" ? "Servicios seleccionados" : "Según reglas del negocio"} />
          <InfoTile icon={CreditCard} label={t("Precio")} value={`${money(price, subscription?.currency || membership.currency || plan.currency || business.currency || 'USD')} / ${cycleLabel(plan.billingCycle || membership.billingCycle)}`} hint={discount > 0 ? `${discount}% en extras` : credits.isUnlimited ? "Sin consumo de créditos" : "Sin extras configurados"} />
          <InfoTile icon={CalendarDays} label={t("Vigencia")} value={`${fmtDate(start)} — ${fmtDate(end)}`} hint={<RenewalHint membership={membership} plan={plan} subscription={subscription} />} />
          <InfoTile icon={ShieldCheck} label={t("Estado")} value={membershipCardStatusLabel(membership, subscription)} hint={subscriptionCancellationScheduled(subscription) ? "Puedes seguir usando tus beneficios hasta terminar el período pagado." : credits.isUnlimited ? "Tu uso ilimitado se valida al reservar" : credits.isDiscountOnly ? "Tu descuento se aplica al reservar" : "Tus créditos se aplican al reservar"} />
        </section>

        {activity.length ? (
          <section className="mmp-activity-ledger is-detail">
            <span>{t("Actividad reciente")}</span>
            {activity.map((entry) => (
              <div className="mmp-activity-row" key={entry.id || `${entry.activityType}-${entry.createdAt?.seconds || entry.title}`}>
                <strong>{activityLine(entry)}</strong>
                <small>{activityDate(entry.createdAt)}{entry.creditsAfter !== null && entry.creditsAfter !== undefined ? ` · quedan ${entry.creditsAfter}` : ""}</small>
              </div>
            ))}
          </section>
        ) : null}

        <button
          type="button"
          className="mmp-book-btn is-modal"
          disabled={!active || !businessRouteParam(row)}
          onClick={() => navigate(bookingPath, { state: { useMembership: true, membershipId: membership.id, planId: plan.id || membership.planId, customerId: row.customerId } })}
        >
          <CalendarDays size={17} /> <span className="mmp-book-label-full">{active ? "Reservar usando membresía" : "Renovación pendiente con el negocio"}</span><span className="mmp-book-label-mobile">{active ? "Reservar" : t("Pendiente")}</span> <ChevronRight size={16} />
        </button>

        {manageable ? (
          <button
            type="button"
            className="mmp-manage-link"
            onClick={() => {
              onClose();
              onManage(row);
            }}
          >{t("Administrar membresía")}<ChevronRight size={16} />
          </button>
        ) : null}

        <p className="mmp-modal-footnote"><ShieldCheck size={15} /> {credits.isUnlimited ? "Tu membresía se validará sin descontar créditos." : credits.isDiscountOnly ? "Tu descuento se aplicará al reservar." : "Tus créditos se aplicarán automáticamente al reservar."}</p>
      </section>
    </div>
  );

  if (typeof document === "undefined") return null;
  return createPortal(modal, document.body);
}

function MembershipCard({ row, subscription, managementMode = false, onOpenDetail, onManage }) {
  useLanguage();
  const navigate = useNavigate();
  const plan = row?.plan || {};
  const membership = row?.membership || {};
  const business = row?.business || {};
  const status = statusValue(membership.status);
  const active = status === "active";
  const credits = membershipCreditStats(membership, plan);
  const unitRemaining = creditUnitLabel(membership.creditUnit || plan.creditUnit, credits.remaining);
  const discount = Number(plan.discountPercent ?? membership.discountPercent ?? 0) || 0;
  const price = plan.price ?? membership.price;
  const bookingPath = buildBookingPath(row);
  const start = membership.startDate || membership.activatedAt || membership.currentPeriodStart;
  const end = subscriptionPeriodEnd(row, subscription);
  const cancellationScheduled = subscriptionCancellationScheduled(subscription);
  const manageable = canManageRecurringSubscription(row, subscription);

  const benefits = [
    credits.isUnlimited ? "Uso ilimitado" : credits.isDiscountOnly ? "Solo descuento" : credits.included > 0 ? `${credits.included} ${creditUnitLabel(membership.creditUnit || plan.creditUnit, credits.included)} incluidos` : null,
    plan.eligibleMode === "selected" ? "Servicios seleccionados" : "Reglas del negocio",
    discount > 0 ? `${discount}% extra` : null,
  ].filter(Boolean);

  return (
    <article className={`mmp-card status-${status} ${cancellationScheduled ? "is-ending" : ""}`}>
      <div className="mmp-card-glow" aria-hidden="true" />
      <div className="mmp-card-top">
        <MembershipBadge />
        <div className="mmp-card-title">
          <strong>{planTitle(row)}</strong>
          <span><Store size={13} /> {business.name || membership.businessName || t("Negocio")}</span>
        </div>
        <em className={`mmp-status status-${status} ${cancellationScheduled ? "is-ending" : ""}`}>{membershipCardStatusLabel(membership, subscription)}</em>
      </div>

      <p>{planDescription(row)}</p>

      <div className="mmp-benefits">
        {benefits.map((benefit) => (
          <span key={benefit}><Sparkles size={13} /> {benefit}</span>
        ))}
      </div>

      <div className={`mmp-meta-strip ${credits.isUnlimited || credits.isDiscountOnly ? "is-simple-benefit" : "is-credits-benefit"}`}>
        <InfoTile icon={CreditCard} label={t("Precio")} value={`${money(price, subscription?.currency || membership.currency || plan.currency || business.currency || 'USD')} / ${cycleLabel(plan.billingCycle || membership.billingCycle)}`} />
        <InfoTile icon={CalendarDays} label={t("Ciclo")} value={cycleText(plan.billingCycle || membership.billingCycle)} />
        {credits.isUnlimited || credits.isDiscountOnly ? null : (
          <InfoTile icon={Ticket} label={t("Beneficio")} value={membershipBenefitName(membership, plan)} />
        )}
      </div>

      {credits.isUnlimited || credits.isDiscountOnly ? (
        <div className={`mmp-benefit-summary ${credits.isDiscountOnly ? "is-discount" : "is-unlimited"}`}>
          <span className="mmp-benefit-summary-icon">
            {credits.isUnlimited ? <Sparkles size={20} /> : <BadgeCheck size={20} />}
          </span>
          <div>
            <span>{credits.isUnlimited ? t("Beneficio incluido") : t("Beneficio de descuento")}</span>
            <strong>
              {credits.isUnlimited
                ? t("Uso ilimitado")
                : discount > 0
                  ? t("{percent}% de descuento", {percent: discount})
                  : t("Descuento activo")}
            </strong>
            <small>
              {credits.isUnlimited
                ? t("Reserva los servicios incluidos sin consumir créditos.")
                : t("El descuento se aplica según los servicios y reglas incluidos en tu plan.")}
            </small>
          </div>
        </div>
      ) : (
<div className="mmp-usage-box is-credits">
        <div className="mmp-usage-head">
          <strong>{t("Créditos del ciclo")}</strong>
          <span>{credits.isUnlimited ? t("Sin límite") : credits.isDiscountOnly ? t("Descuento") : `${credits.usedPct}% utilizado`}</span>
        </div>

        {credits.isUnlimited ? (
          <div className="mmp-unlimited-inline">
            <Sparkles size={20} />
            <div>
              <strong>{t("Uso ilimitado")}</strong>
              <small>{t("Mientras esté activa, puedes reservar servicios incluidos sin consumir créditos.")}</small>
            </div>
          </div>
        ) : credits.isDiscountOnly ? (
          <div className="mmp-unlimited-inline">
            <BadgeCheck size={20} />
            <div>
              <strong>{discount > 0 ? t("{percent}% de descuento", {percent: discount}) : t("Descuento activo")}</strong>
              <small>{t("Este beneficio no maneja créditos disponibles.")}</small>
            </div>
          </div>
        ) : (
          <>
            <div className="mmp-credit-hero">
              <div>
                <span>{t("Disponibles")}</span>
                <strong>{credits.remaining}</strong>
                <small>{unitRemaining}</small>
              </div>
              <div>
                <span>{t("Usados")}</span>
                <strong>{credits.used}</strong>
                <small>{t("de") + " "}{credits.included || credits.remaining}</small>
              </div>
            </div>
            <div className="mmp-progress" aria-label={t("{percent}% del ciclo utilizado", {percent: credits.usedPct})}>
              <i style={{ width: `${credits.usedPct}%` }} />
            </div>
          </>
        )}
      </div>
      )}

<div className="mmp-vigency-row">
        <div>
          <span>{cancellationScheduled ? t("Activa hasta") : t("Vigencia")}</span>
          <strong>{fmtDate(start)} — {fmtDate(end)}</strong>
          <small><RenewalHint membership={membership} plan={plan} subscription={subscription} /></small>
        </div>
        <ChevronRight size={18} />
      </div>

      <div className="mmp-card-actions">
        <button type="button" className="mmp-secondary-btn" onClick={() => onOpenDetail(row)}>
          {managementMode ? t("Ver beneficios") : t("Gestionar")}
        </button>
        {managementMode ? (
          <button
            type="button"
            className="mmp-book-btn"
            onClick={() => onManage(row)}
          >
            <ShieldCheck size={16} /> <span>Administrar</span>
          </button>
        ) : (
          <button
            type="button"
            className="mmp-book-btn"
            disabled={!active || !businessRouteParam(row)}
            onClick={() => navigate(bookingPath, { state: { useMembership: true, membershipId: membership.id, planId: plan.id || membership.planId, customerId: row.customerId } })}
          >
            <CalendarDays size={16} /> <span className="mmp-book-label-full">{active ? "Reservar usando membresía" : "Renovación pendiente"}</span><span className="mmp-book-label-mobile">{active ? "Reservar" : t("Pendiente")}</span>
          </button>
        )}
      </div>
    </article>
  );
}

function MembershipManagementModal({
  row,
  subscription,
  subscriptionsLoaded,
  subscriptionError,
  actionState,
  actionMessage,
  onClose,
  onCancel,
  onResume,
  onRefresh,
}) {
  useLanguage();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const plan = row?.plan || {};
  const membership = row?.membership || {};
  const business = row?.business || {};
  const subId = subscriptionIdFromRow(row, subscription);
  const recurring = canManageRecurringSubscription(row, subscription);
  const cancellationScheduled = subscriptionCancellationScheduled(subscription);
  const status = subscriptionStatus(subscription?.status);
  const closed = ["cancelled", "expired"].includes(status);
  const end = subscriptionPeriodEnd(row, subscription);
  const price = subscription?.amount ?? plan.price ?? membership.price;
  const busy = Boolean(actionState && actionState === subId);
  const manageStateReady = recurring && Boolean(subscription);
  const businessName = business.name || membership.businessName || "Negocio";

  const modal = (
    <div className="mmp-modal-layer" role="dialog" aria-modal="true" aria-label={t("Administrar membresía")}>
      <div className="mmp-modal-backdrop" onClick={busy ? undefined : onClose} />
      <section className="mmp-modal-card mmp-manage-modal">
        <button type="button" className="mmp-modal-close" onClick={onClose} disabled={busy} aria-label={t("Cerrar")}><X size={20} /></button>

        <div className="mmp-detail-head">
          <MembershipBadge large />
          <div className="mmp-detail-title">
            <span>{t("Administrar membresía")}</span>
            <h2>{planTitle(row)}</h2>
            <p><Store size={14} /> {businessName}</p>
          </div>
          {manageStateReady ? (
            <em className={`mmp-status ${cancellationScheduled ? "is-ending" : "status-active"}`}>
              {cancellationScheduled ? t("No renueva") : subscriptionStatusLabel(subscription)}
            </em>
          ) : null}
        </div>

        {!subscriptionsLoaded ? (
          <div className="mmp-manage-loading"><RefreshCw size={17} />{" " + t("Cargando estado de renovación...")}</div>
        ) : subscriptionError && !subscription ? (
          <div className="mmp-manage-warning">
            <strong>{t("No pudimos confirmar el estado de renovación.")}</strong>
            <span>{t("Tus beneficios no cambian. Actualiza antes de intentar administrar esta membresía.")}</span>
            <button type="button" onClick={onRefresh}>{t("Actualizar estado")}</button>
          </div>
        ) : !recurring ? (
          <div className="mmp-manage-neutral">
            <ShieldCheck size={20} />
            <div>
              <strong>{t("Sin renovación automática de Loop")}</strong>
              <span>{t("Esta membresía fue asignada o administrada directamente por el negocio. No hay un cobro recurrente que cancelar desde aquí.")}</span>
            </div>
          </div>
        ) : !subscription ? (
          <div className="mmp-manage-warning">
            <strong>{t("No encontramos la suscripción vinculada.")}</strong>
            <span>{t("No haremos ningún cambio hasta poder leer el estado exacto de la renovación.")}</span>
            <button type="button" onClick={onRefresh}>{t("Actualizar estado")}</button>
          </div>
        ) : (
          <>
            <section className="mmp-manage-grid is-compact">
              <InfoTile
                icon={CreditCard}
                label={t("Precio")}
                value={`${money(price, subscription?.currency || membership.currency || plan.currency || business.currency || 'USD')} / ${cycleLabel(subscription?.billingCycle || plan.billingCycle || membership.billingCycle)}`}
                hint={subscriptionPaymentLabel(subscription)}
              />
              <InfoTile
                icon={CalendarDays}
                label={cancellationScheduled ? t("Activa hasta") : "Pr\u00f3xima renovaci\u00f3n"}
                value={fmtDate(end)}
                hint={cancellationScheduled ? "Sin nuevos cobros despu\u00e9s de esta fecha." : "Se procesa autom\u00e1ticamente."}
              />
            </section>

            {actionMessage ? <div className="mmp-action-message">{t(actionMessage)}</div> : null}

            {cancellationScheduled ? (
              <section className="mmp-renewal-control is-resume">
                <div>
                  <span>{"Renovaci\u00f3n autom\u00e1tica"}</span>
                  <strong>{t("Cancelada")}</strong>
                  <p>{"No habr\u00e1 nuevos cobros. Puedes reactivarla mientras tu membres\u00eda siga vigente."}</p>
                </div>
                <button type="button" className="mmp-resume-btn" disabled={busy || closed} onClick={() => onResume(subId)}>
                  {busy ? "Actualizando..." : "Reactivar renovaci\u00f3n"}
                </button>
              </section>
            ) : closed ? (
              <section className="mmp-renewal-control is-closed">
                <div>
                  <span>{t("Suscripción finalizada")}</span>
                  <strong>{t("Esta suscripción ya no tiene una renovación activa.")}</strong>
                  <p>{t("Si quieres volver a tener el plan, deberás adquirirlo nuevamente desde el negocio.")}</p>
                </div>
              </section>
            ) : confirmCancel ? (
              <section className="mmp-cancel-confirm">
                <div>
                  <span>{t("Confirmar cambio")}</span>
                  <strong>{t("¿Cancelar la renovación automática?")}</strong>
                  <p>{t("Tu membresía seguirá activa hasta") + " "}<b>{fmtDate(end)}</b>{t(". Después de esa fecha no se realizarán más cobros.")}</p>
                </div>
                <div className="mmp-confirm-actions">
                  <button type="button" className="mmp-secondary-btn" disabled={busy} onClick={() => setConfirmCancel(false)}>{" " + t("Volver")}</button>
                  <button type="button" className="mmp-cancel-confirm-btn" disabled={busy} onClick={() => onCancel(subId).then((ok) => { if (ok) setConfirmCancel(false); })}>
                    {busy ? "Cancelando..." : "Cancelar renovaci\u00f3n"}
                  </button>
                </div>
              </section>
            ) : (
              <section className="mmp-renewal-control">
                <div>
                  <span>{"Renovaci\u00f3n autom\u00e1tica"}</span>
                  <strong>{t("Activada")}</strong>
                  <p>{"Se renovar\u00e1 el "}{fmtDate(end)}{". Si cancelas, conservar\u00e1s tus beneficios hasta esa fecha."}</p>
                </div>
                <button type="button" className="mmp-cancel-renewal-btn" disabled={busy} onClick={() => setConfirmCancel(true)}>
                  {"Cancelar renovaci\u00f3n"}
                </button>
              </section>
            )}
          </>
        )}

        <p className="mmp-modal-footnote"><ShieldCheck size={15} /> {"No perder\u00e1s el per\u00edodo que ya pagaste."}</p>
      </section>
    </div>
  );

  if (typeof document === "undefined") return null;
  return createPortal(modal, document.body);
}

export default function MyMembershipsPage({ embedded = false, managementMode = false } = {}) {
  useLanguage();
  const navigate = useNavigate();
  const [rows, setRows] = useState(() => readMembershipsCache()?.items || []);
  const [loading, setLoading] = useState(() => !(readMembershipsCache()?.items || []).length);
  const [refreshing, setRefreshing] = useState(false);
  const [loadedFromCache, setLoadedFromCache] = useState(() => Boolean((readMembershipsCache()?.items || []).length));
  const [error, setError] = useState("");
  const [debugInfo, setDebugInfo] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [managementRow, setManagementRow] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionsLoaded, setSubscriptionsLoaded] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState("");
  const [actionState, setActionState] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const actionLockRef = useRef(false);
  const debugMode = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("debugMemberships") === "1";

  const load = async ({ manual = false } = {}) => {
    const hasRows = rows.length > 0 || (readMembershipsCache()?.items || []).length > 0;
    setLoading(!hasRows);
    setRefreshing(hasRows);
    setError("");
    try {
      const res = await callCallableNativeSafe({
        functionName: "getMyMemberships",
        data: { limit: 80, activityLimit: 24, debug: debugMode, cacheBust: Date.now() },
        timeoutMs: 45000,
      });
      const items = Array.isArray(res?.data?.items) ? res.data.items : [];
      if (debugMode) {
        console.log("[getMyMemberships result]", res?.data);
        setDebugInfo(res?.data || null);
      }
      setRows(items);
      writeMembershipsCache(items);
      setLoadedFromCache(false);
      return items;
    } catch (e) {
      console.error("[MyMembershipsPage] failed", e);
      const cached = readMembershipsCache();
      if (cached?.items?.length) {
        setRows(cached.items);
        setLoadedFromCache(true);
        setError(manual ? "No pudimos actualizar; mostrando tu último estado guardado." : "");
        return cached.items;
      }
      const raw = String(e?.code || e?.message || e || "").toLowerCase();
      setError(
        raw.includes("timeout")
          ? "La conexión tardó demasiado. Pulsa Actualizar para intentarlo nuevamente."
          : raw.includes("unauthenticated") || raw.includes("auth-token-missing")
            ? "Inicia sesión nuevamente para ver tus membresías."
            : "No pudimos cargar tus membresías.",
      );
      return [];
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadSubscriptionState = async ({ silent = false } = {}) => {
    if (!silent) setSubscriptionError("");
    try {
      const res = await callCallableNativeSafe({
        functionName: "getMyMembershipSubscriptions",
        requireAuth: true,
        requireAppCheck: true,
        data: { limit: 100 },
        timeoutMs: 30000,
      });
      const items = Array.isArray(res?.data?.items) ? res.data.items : [];
      setSubscriptions(items);
      setSubscriptionsLoaded(true);
      setSubscriptionError("");
      return { ok: true, items };
    } catch (e) {
      console.warn("[MyMembershipsPage] subscription management state unavailable", e);
      setSubscriptionsLoaded(true);
      setSubscriptionError(
        callableMessage(
          e,
          "No pudimos cargar el estado de renovación automática."
        )
      );
      return { ok: false, items: null, error: e };
    }
  };

  const refreshAll = async ({ manual = true } = {}) => {
    await Promise.all([
      load({ manual }),
      loadSubscriptionState(),
    ]);
  };

  useEffect(() => {
    load();
    loadSubscriptionState();
  }, []);

  const summary = useMemo(() => {
    const active = rows.filter((row) => statusValue(row?.membership?.status) === "active");
    const credits = active.reduce((sum, row) => sum + membershipCreditStats(row?.membership, row?.plan).remaining, 0);
    const unlimited = active.filter((row) => membershipCreditStats(row?.membership, row?.plan).isUnlimited).length;
    return { activeCount: active.length, credits, unlimited };
  }, [rows]);

  const updateSubscriptionInMemory = (updated) => {
    if (!updated || !updated.id) return;
    setSubscriptions((current) => {
      const found = current.some((item) => String(item?.id || "") === String(updated.id));
      return found
        ? current.map((item) => String(item?.id || "") === String(updated.id) ? { ...item, ...updated } : item)
        : [{ ...updated }, ...current];
    });
  };

  const readbackActionState = async (subscriptionId, expectedCancellationScheduled) => {
    const refreshed = await loadSubscriptionState({ silent: true });
    if (!refreshed.ok || !Array.isArray(refreshed.items)) return false;
    const current = refreshed.items.find((item) => String(item?.id || "") === String(subscriptionId));
    if (!current) return false;
    return subscriptionCancellationScheduled(current) === expectedCancellationScheduled;
  };

  const handleCancelRenewal = async (subscriptionId) => {
    const cleanId = String(subscriptionId || "").trim();
    if (!cleanId || actionLockRef.current) return false;

    actionLockRef.current = true;
    setActionState(cleanId);
    setActionMessage("");

    try {
      const res = await callCallableNativeSafe({
        functionName: "cancelMyMembershipSubscription",
        requireAuth: true,
        requireAppCheck: true,
        data: {
          subscriptionId: cleanId,
          reason: "Cancelación solicitada por el cliente desde Perfil > Membresías",
        },
        timeoutMs: 30000,
      });
      const updated = res?.data?.subscription || null;
      if (updated?.id) updateSubscriptionInMemory(updated);
      else await loadSubscriptionState({ silent: true });
      setActionMessage("Renovación cancelada. Mantendrás tus beneficios hasta el final del período pagado.");
      return true;
    } catch (e) {
      console.warn("[MyMembershipsPage] cancellation result uncertain", e);
      const committed = await readbackActionState(cleanId, true);
      if (committed) {
        setActionMessage("Renovación cancelada. El estado fue confirmado al volver a consultar tu suscripción.");
        return true;
      }
      setActionMessage("No pudimos confirmar la cancelación. No vuelvas a presionar el botón hasta actualizar el estado de la membresía.");
      return false;
    } finally {
      actionLockRef.current = false;
      setActionState("");
    }
  };

  const handleResumeRenewal = async (subscriptionId) => {
    const cleanId = String(subscriptionId || "").trim();
    if (!cleanId || actionLockRef.current) return false;

    actionLockRef.current = true;
    setActionState(cleanId);
    setActionMessage("");

    try {
      const res = await callCallableNativeSafe({
        functionName: "resumeMyMembershipSubscription",
        requireAuth: true,
        requireAppCheck: true,
        data: { subscriptionId: cleanId },
        timeoutMs: 30000,
      });
      const updated = res?.data?.subscription || null;
      if (updated?.id) updateSubscriptionInMemory(updated);
      else await loadSubscriptionState({ silent: true });
      setActionMessage("Renovación automática reactivada.");
      return true;
    } catch (e) {
      console.warn("[MyMembershipsPage] resume result uncertain", e);
      const committed = await readbackActionState(cleanId, false);
      if (committed) {
        setActionMessage("Renovación reactivada. El estado fue confirmado al volver a consultar tu suscripción.");
        return true;
      }
      setActionMessage("No pudimos confirmar la reactivación. Actualiza el estado antes de volver a intentarlo.");
      return false;
    } finally {
      actionLockRef.current = false;
      setActionState("");
    }
  };

  const selectedSubscription = selectedRow ? subscriptionForRow(selectedRow, subscriptions) : null;
  const managedSubscription = managementRow ? subscriptionForRow(managementRow, subscriptions) : null;
  const PageTag = embedded ? "section" : "main";

  return (
    <PageTag className={`mmp-page ${embedded ? "mmp-page-embedded" : ""} ${managementMode ? "mmp-management-mode" : ""}`}>
      <style>{styles}</style>

      <div className="mmp-head">
        <button type="button" onClick={() => navigate(-1)}><ArrowLeft size={16} />{" " + t("Volver")}</button>
        <button type="button" onClick={() => refreshAll({ manual: true })} disabled={loading || refreshing}><RefreshCw size={16} /> {t("Actualizar")}</button>
      </div>

      <section className="mmp-hero">
        <div className="mmp-title">
          <span>Loop</span>
          <h1>{t("Mis membresías")}</h1>
          <p>{t("Consulta tus créditos, beneficios y vigencia. Reserva con tu plan activo desde el negocio correspondiente.")}</p>
        </div>
        <div className="mmp-summary">
          <span>{summary.activeCount}{" " + t("activa")}{summary.activeCount === 1 ? "" : "s"}</span>
          <span>{summary.unlimited > 0 ? `${summary.unlimited} ilimitada${summary.unlimited === 1 ? "" : "s"}` : `${summary.credits} crédito${summary.credits === 1 ? "" : "s"} disponible${summary.credits === 1 ? "" : "s"}`}</span>
        </div>
      </section>

      {embedded ? (
        <div className="mmp-embedded-toolbar">
          <div>
            <span>{t("Planes y renovaciones")}</span>
            <strong>{t("Administra tus membresías activas y decide cuáles se renuevan automáticamente.")}</strong>
          </div>
          <button type="button" onClick={() => refreshAll({ manual: true })} disabled={loading || refreshing}>
            <RefreshCw size={15} /> {t("Actualizar")}
          </button>
        </div>
      ) : null}

      <BenefitsLoading busy={loading || refreshing} hasRows={rows.length > 0} error={t(error)} onRetry={() => refreshAll({manual:true})} title={t("membresías")} />
      {managementMode && subscriptionError ? (
        <div className="mmp-note is-warning">
          <ShieldCheck size={15} />{t("No pudimos leer el estado de renovación de todas tus membresías. No se hará ningún cambio hasta confirmarlo.")}</div>
      ) : null}
      {!loading && !refreshing && !error && rows.length === 0 ? (
        <div className="mmp-empty is-polished">
          <div><Crown size={22} /></div>
          <strong>{t("Todavía no tienes membresías.")}</strong>
          <span>{t("Cuando un negocio te asigne o renueve una, aparecerá aquí.")}</span>
        </div>
      ) : null}
      {debugMode && debugInfo ? (
        <pre className="mmp-empty" style={{ whiteSpace: "pre-wrap", fontSize: 12, overflow: "auto" }}>
{JSON.stringify({
  matchedCustomers: debugInfo.matchedCustomers,
  matchedDirectMemberships: debugInfo.matchedDirectMemberships,
  items: Array.isArray(debugInfo.items) ? debugInfo.items.length : 0,
  subscriptions: subscriptions.length,
  subscriptionError,
  debug: debugInfo.debug,
}, null, 2)}
        </pre>
      ) : null}

      <div className="mmp-list">
        {rows.map((row) => {
          const subscription = subscriptionForRow(row, subscriptions);
          return (
            <MembershipCard
              key={row.membership?.id || `${row.businessId}-${row.plan?.id}`}
              row={row}
              subscription={subscription}
              managementMode={managementMode}
              onOpenDetail={setSelectedRow}
              onManage={(nextRow) => {
                setActionMessage("");
                setManagementRow(nextRow);
              }}
            />
          );
        })}
      </div>

      {selectedRow ? (
        <MembershipDetailModal
          row={selectedRow}
          subscription={selectedSubscription}
          onClose={() => setSelectedRow(null)}
          onManage={(nextRow) => {
            setActionMessage("");
            setManagementRow(nextRow);
          }}
        />
      ) : null}

      {managementRow ? (
        <MembershipManagementModal
          row={managementRow}
          subscription={managedSubscription}
          subscriptionsLoaded={subscriptionsLoaded}
          subscriptionError={t(subscriptionError)}
          actionState={actionState}
          actionMessage={t(actionMessage)}
          onClose={() => {
            if (!actionState) {
              setActionMessage("");
              setManagementRow(null);
            }
          }}
          onCancel={handleCancelRenewal}
          onResume={handleResumeRenewal}
          onRefresh={() => loadSubscriptionState()}
        />
      ) : null}
    </PageTag>
  );
}

const styles = `
  .mmp-page {
    min-height: 100dvh;
    background:
      radial-gradient(circle at top left, rgba(0,103,95,.10), transparent 34%),
      linear-gradient(180deg, #f8fafc, #fff);
    padding: 20px;
    max-width: 1120px;
    margin: 0 auto;
    color: #0f172a;
    box-sizing: border-box;
  }

  .mmp-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 18px;
  }

  .mmp-head button,
  .mmp-secondary-btn {
    border: 1px solid #dbe4ef;
    background: rgba(255,255,255,.96);
    border-radius: 999px;
    min-height: 42px;
    padding: 0 15px;
    font-weight: 950;
    color: #334155;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    cursor: pointer;
    box-shadow: 0 10px 25px rgba(15,23,42,.045);
  }

  .mmp-hero {
    border: 1px solid rgba(226,232,240,.95);
    border-radius: 30px;
    background: linear-gradient(135deg, #ffffff, rgba(240,253,250,.82));
    padding: 22px;
    margin-bottom: 16px;
    box-shadow: 0 18px 45px rgba(15,23,42,.06);
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 14px;
  }

  .mmp-title span {
    display: block;
    color: #00675f;
    font-size: 11px;
    font-weight: 950;
    letter-spacing: .14em;
    text-transform: uppercase;
  }

  .mmp-title h1 {
    margin: 4px 0 0;
    font-size: 34px;
    letter-spacing: -.06em;
    line-height: 1;
  }

  .mmp-title p {
    margin: 9px 0 0;
    color: #64748b;
    font-size: 14px;
    font-weight: 750;
    max-width: 560px;
    line-height: 1.45;
  }

  .mmp-summary {
    display: flex;
    gap: 9px;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .mmp-summary span,
  .mmp-note {
    border: 1px solid rgba(20,184,166,.22);
    background: rgba(240,253,250,.92);
    color: #00675f;
    border-radius: 999px;
    padding: 8px 11px;
    font-size: 12px;
    font-weight: 950;
    display: inline-flex;
    align-items: center;
    gap: 7px;
  }

  .mmp-note { margin: 0 0 12px; }

  .mmp-empty,
  .mmp-error {
    border: 1px solid #e2e8f0;
    border-radius: 24px;
    background: #fff;
    padding: 22px;
    color: #64748b;
    font-weight: 850;
    box-shadow: 0 16px 38px rgba(15,23,42,.06);
  }

  .mmp-empty.is-polished {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .mmp-empty.is-polished > div {
    width: 42px;
    height: 42px;
    border-radius: 16px;
    background: #ecfdf7;
    color: #00675f;
    display: grid;
    place-items: center;
    flex: 0 0 auto;
  }

  .mmp-empty.is-polished strong { color: #0f172a; margin-right: 3px; }
  .mmp-empty.is-polished span { color: #64748b; }

  .mmp-error { background: #fff7ed; border-color: #fed7aa; color: #c2410c; }

  .mmp-list {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .mmp-card {
    position: relative;
    overflow: hidden;
    border: 1px solid rgba(209,225,224,.95);
    border-radius: 28px;
    background: linear-gradient(180deg, #fff, #fbfefd);
    padding: 18px;
    box-shadow: 0 18px 44px rgba(15,23,42,.065);
  }

  .mmp-card-glow {
    position: absolute;
    inset: auto -70px -85px auto;
    width: 220px;
    height: 220px;
    border-radius: 999px;
    background: rgba(20,184,166,.16);
    pointer-events: none;
  }

  .mmp-card-top,
  .mmp-detail-head {
    position: relative;
    display: flex;
    align-items: center;
    gap: 13px;
  }

  .mmp-badge {
    width: 48px;
    height: 48px;
    border-radius: 18px;
    background: linear-gradient(135deg, #00675f, #07887e);
    color: #fff;
    display: grid;
    place-items: center;
    box-shadow: 0 16px 30px rgba(0,103,95,.19);
    flex: 0 0 auto;
  }

  .mmp-badge.is-large {
    width: 72px;
    height: 72px;
    border-radius: 24px;
  }

  .mmp-card-title,
  .mmp-detail-title { min-width: 0; }

  .mmp-card-title strong,
  .mmp-detail-title h2 {
    display: block;
    font-size: 20px;
    font-weight: 950;
    letter-spacing: -.035em;
    color: #0f172a;
  }

  .mmp-detail-title h2 { margin: 3px 0 0; font-size: 28px; line-height: 1; }
  .mmp-detail-title > span { color: #00675f; font-size: 10px; font-weight: 950; letter-spacing: .14em; text-transform: uppercase; }

  .mmp-card-title span,
  .mmp-detail-title p {
    display: flex;
    align-items: center;
    gap: 5px;
    color: #64748b;
    font-size: 12.5px;
    font-weight: 850;
    margin: 4px 0 0;
  }

  .mmp-status {
    margin-left: auto;
    font-style: normal;
    border-radius: 999px;
    background: #f0fdfa;
    color: #00675f;
    padding: 8px 11px;
    font-size: 12px;
    font-weight: 950;
    flex: 0 0 auto;
  }

  .mmp-status.status-active { background: rgba(22,163,74,.10); color: #15803d; }
  .mmp-status.status-cancelled,
  .mmp-status.status-expired { background: #e2e8f0; color: #475569; }
  .mmp-status.status-paused { background: #fef3c7; color: #92400e; }

  .mmp-card p,
  .mmp-detail-description {
    position: relative;
    color: #64748b;
    font-size: 13.5px;
    line-height: 1.45;
    font-weight: 760;
    margin: 12px 0 0;
  }

  .mmp-benefits {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 13px;
  }

  .mmp-benefits span {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    border-radius: 999px;
    background: #f0fdfa;
    color: #00675f;
    padding: 8px 10px;
    font-size: 12px;
    font-weight: 950;
  }

  .mmp-meta-strip {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
    margin-top: 14px;
  }

  .mmp-info-tile {
    border: 1px solid #e2e8f0;
    border-radius: 20px;
    background: rgba(248,250,252,.82);
    padding: 12px;
    display: flex;
    gap: 10px;
    align-items: flex-start;
    min-width: 0;
  }

  .mmp-info-icon {
    width: 32px;
    height: 32px;
    border-radius: 12px;
    display: grid;
    place-items: center;
    background: #ecfdf7;
    color: #00675f;
    flex: 0 0 auto;
  }

  .mmp-info-tile span,
  .mmp-credit-hero span,
  .mmp-vigency-row span {
    display: block;
    color: #8a9ab0;
    font-size: 10.5px;
    font-weight: 950;
    text-transform: uppercase;
    letter-spacing: .08em;
  }

  .mmp-info-tile strong,
  .mmp-vigency-row strong {
    display: block;
    margin-top: 4px;
    color: #0f172a;
    font-size: 13px;
    font-weight: 950;
  }

  .mmp-info-tile small,
  .mmp-vigency-row small {
    display: block;
    margin-top: 4px;
    color: #64748b;
    font-size: 12px;
    font-weight: 780;
  }

  .mmp-info-tile small span,
  .mmp-vigency-row small span {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    text-transform: none;
    letter-spacing: 0;
    color: #64748b;
    font-size: 12px;
    font-weight: 820;
  }

  .mmp-usage-box {
    margin-top: 14px;
    border: 1px solid rgba(226,232,240,.96);
    border-radius: 22px;
    padding: 12px;
    background: #fff;
  }

  .mmp-usage-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;
  }

  .mmp-usage-head strong { font-size: 13px; font-weight: 950; }
  .mmp-usage-head span { color: #64748b; font-size: 12px; font-weight: 850; }

  .mmp-credit-hero {
    position: relative;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin: 0 0 12px;
  }

  .mmp-credit-hero > div {
    border: 1px solid #e2e8f0;
    border-radius: 18px;
    background: #f8fafc;
    padding: 12px;
  }

  .mmp-credit-hero strong {
    display: inline-block;
    margin-top: 4px;
    font-size: 28px;
    line-height: 1;
    font-weight: 950;
    color: #0f172a;
  }

  .mmp-credit-hero small {
    margin-left: 6px;
    color: #64748b;
    font-weight: 900;
  }

  .mmp-progress {
    position: relative;
    height: 11px;
    border-radius: 999px;
    background: #e2e8f0;
    overflow: hidden;
  }

  .mmp-progress i {
    display: block;
    height: 100%;
    border-radius: 999px;
    background: linear-gradient(135deg, #00675f, #14b8a6);
  }

  .mmp-progress.is-detail { margin-top: 14px; }
  .mmp-progress-note { margin: 8px 0 0; color: #64748b; font-size: 12px; font-weight: 850; text-align: center; }

  .mmp-unlimited-box {
    border: 1px solid rgba(0,103,95,.16);
    border-radius: 22px;
    background:
      radial-gradient(circle at 92% 10%, rgba(0,103,95,.12), transparent 34%),
      linear-gradient(135deg, rgba(240,253,250,.95), rgba(255,255,255,.98));
    padding: 18px;
    box-shadow: inset 0 1px 0 rgba(255,255,255,.8);
  }

  .mmp-unlimited-box span {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    color: #00675f;
    font-size: 12px;
    font-weight: 950;
    text-transform: uppercase;
    letter-spacing: .12em;
  }

  .mmp-unlimited-box strong {
    display: block;
    margin-top: 9px;
    color: #0f172a;
    font-size: 18px;
    line-height: 1.25;
  }

  .mmp-unlimited-box p {
    margin: 7px 0 0;
    color: #64748b;
    font-size: 13px;
    line-height: 1.45;
    font-weight: 750;
  }

  .mmp-unlimited-inline {
    min-height: 92px;
    border-radius: 20px;
    background:
      radial-gradient(circle at 88% 10%, rgba(0,103,95,.10), transparent 36%),
      linear-gradient(135deg, #f0fdfa, #ffffff);
    border: 1px solid rgba(0,103,95,.14);
    display: flex;
    align-items: center;
    gap: 13px;
    padding: 16px;
    color: #00675f;
  }

  .mmp-unlimited-inline div {
    min-width: 0;
  }

  .mmp-unlimited-inline strong {
    display: block;
    color: #0f172a;
    font-size: 17px;
    font-weight: 950;
  }

  .mmp-unlimited-inline small {
    display: block;
    margin-top: 4px;
    color: #64748b;
    font-size: 12px;
    line-height: 1.35;
    font-weight: 800;
  }

  .mmp-vigency-row {
    margin-top: 14px;
    display: flex;
    justify-content: space-between;
    gap: 12px;
    align-items: center;
    border-top: 1px solid rgba(226,232,240,.92);
    padding-top: 14px;
  }

  .mmp-card-actions {
    display: grid;
    grid-template-columns: minmax(160px, .55fr) minmax(220px, 1fr);
    gap: 10px;
    margin-top: 14px;
  }

  .mmp-book-btn {
    position: relative;
    width: 100%;
    border: 0;
    border-radius: 999px;
    background: linear-gradient(135deg, #00675f, #08786f);
    color: #fff;
    min-height: 46px;
    padding: 0 18px;
    font-size: 13.5px;
    font-weight: 950;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    cursor: pointer;
    box-shadow: 0 16px 30px rgba(0,103,95,.18);
  }

  .mmp-book-btn:disabled {
    background: #cbd5e1;
    box-shadow: none;
    cursor: not-allowed;
  }

  .mmp-book-label-mobile { display: none; }

  .mmp-activity-ledger {
    margin-top: 12px;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    border-radius: 18px;
    padding: 10px;
  }

  .mmp-activity-ledger > span {
    display: block;
    color: #00675f;
    font-size: 10px;
    font-weight: 950;
    letter-spacing: .1em;
    text-transform: uppercase;
    margin-bottom: 6px;
  }

  .mmp-activity-row {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 0;
    border-top: 1px solid rgba(226,232,240,.85);
  }

  .mmp-activity-row:first-of-type { border-top: 0; }
  .mmp-activity-row strong { font-size: 12px; color: #0f172a; font-weight: 900; }
  .mmp-activity-row small { font-size: 11px; color: #64748b; font-weight: 800; text-align: right; white-space: nowrap; }

  .mmp-card.status-cancelled,
  .mmp-card.status-expired { opacity: .72; background: #f8fafc; }

  .mmp-modal-layer {
    position: fixed;
    inset: 0;
    z-index: 1000000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 22px;
  }

  .mmp-modal-backdrop {
    position: absolute;
    inset: 0;
    background: rgba(15,23,42,.34);
    backdrop-filter: blur(13px);
    -webkit-backdrop-filter: blur(13px);
  }

  .mmp-modal-card {
    position: relative;
    width: min(720px, calc(100vw - 28px));
    max-height: calc(100dvh - 44px);
    overflow: auto;
    border-radius: 32px;
    background: linear-gradient(180deg, #fff, #fbfefd);
    border: 1px solid rgba(226,232,240,.96);
    box-shadow: 0 34px 90px rgba(15,23,42,.22);
    padding: 24px;
  }

  .mmp-modal-close {
    position: absolute;
    top: 18px;
    right: 18px;
    width: 42px;
    height: 42px;
    border-radius: 999px;
    border: 0;
    background: #f8fafc;
    color: #0f172a;
    cursor: pointer;
    display: grid;
    place-items: center;
  }

  .mmp-detail-head { padding-right: 54px; }
  .mmp-detail-description { margin-top: 16px; }

  .mmp-detail-section {
    margin-top: 18px;
    border: 1px solid #e2e8f0;
    border-radius: 24px;
    background: #fff;
    padding: 16px;
  }

  .mmp-section-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
  }

  .mmp-section-heading h3 {
    margin: 0;
    font-size: 17px;
    font-weight: 950;
    letter-spacing: -.02em;
  }

  .mmp-section-heading span {
    color: #64748b;
    font-size: 12px;
    font-weight: 900;
  }

  .mmp-detail-usage-grid,
  .mmp-detail-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  .mmp-detail-grid { margin-top: 12px; }
  .mmp-book-btn.is-modal { margin-top: 18px; border-radius: 18px; min-height: 54px; }

  .mmp-modal-footnote {
    margin: 12px 0 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #64748b;
    font-size: 12.5px;
    font-weight: 850;
  }


  .mmp-status.is-ending {
    background: #fff7ed;
    color: #b45309;
  }

  .mmp-card.is-ending {
    border-color: rgba(245,158,11,.22);
  }

  .mmp-manage-link {
    width: 100%;
    margin-top: 10px;
    border: 0;
    background: transparent;
    color: #00675f;
    font-size: 13px;
    font-weight: 950;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    cursor: pointer;
  }

  .mmp-renewal-inline {
    margin-top: 14px;
    border: 1px solid rgba(0,103,95,.16);
    border-radius: 18px;
    background: #f0fdfa;
    color: #00675f;
    padding: 12px 14px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .mmp-renewal-inline.is-ending {
    background: #fff7ed;
    border-color: #fed7aa;
    color: #b45309;
  }

  .mmp-renewal-inline span,
  .mmp-renewal-control span,
  .mmp-cancel-confirm span {
    display: block;
    color: currentColor;
    font-size: 10px;
    font-weight: 950;
    text-transform: uppercase;
    letter-spacing: .1em;
  }

  .mmp-renewal-inline strong {
    display: block;
    margin-top: 3px;
    color: #0f172a;
    font-size: 14px;
    font-weight: 950;
  }

  .mmp-renewal-inline small {
    display: block;
    margin-top: 3px;
    color: #64748b;
    font-size: 11.5px;
    font-weight: 800;
  }

  .mmp-embedded-toolbar {
    margin-bottom: 14px;
    border: 1px solid #e2e8f0;
    border-radius: 20px;
    background: linear-gradient(180deg, #fff, #fbfefd);
    padding: 14px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
  }

  .mmp-embedded-toolbar span {
    display: block;
    color: #00675f;
    font-size: 10px;
    font-weight: 950;
    letter-spacing: .12em;
    text-transform: uppercase;
  }

  .mmp-embedded-toolbar strong {
    display: block;
    margin-top: 4px;
    color: #0f172a;
    font-size: 14px;
    line-height: 1.35;
  }

  .mmp-embedded-toolbar button,
  .mmp-manage-warning button {
    border: 1px solid rgba(0,103,95,.18);
    border-radius: 999px;
    background: #fff;
    color: #00675f;
    min-height: 38px;
    padding: 0 13px;
    font-weight: 950;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    cursor: pointer;
    flex: 0 0 auto;
  }

  .mmp-note.is-warning {
    background: #fff7ed;
    border-color: #fed7aa;
    color: #9a3412;
  }

  .mmp-manage-modal {
    width: min(760px, calc(100vw - 28px));
  }

  .mmp-manage-grid {
    margin-top: 18px;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .mmp-manage-loading,
  .mmp-manage-warning,
  .mmp-manage-neutral,
  .mmp-action-message {
    margin-top: 18px;
    border: 1px solid #e2e8f0;
    border-radius: 20px;
    background: #f8fafc;
    padding: 16px;
    color: #64748b;
    font-size: 13px;
    font-weight: 850;
  }

  .mmp-manage-loading,
  .mmp-manage-neutral {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .mmp-manage-warning {
    background: #fff7ed;
    border-color: #fed7aa;
    color: #9a3412;
  }

  .mmp-manage-warning strong,
  .mmp-manage-neutral strong {
    display: block;
    color: #0f172a;
    font-size: 14px;
    font-weight: 950;
  }

  .mmp-manage-warning span,
  .mmp-manage-neutral span {
    display: block;
    margin-top: 4px;
    line-height: 1.45;
  }

  .mmp-manage-warning button {
    margin-top: 12px;
  }

  .mmp-action-message {
    background: #f0fdfa;
    border-color: rgba(0,103,95,.16);
    color: #00675f;
  }

  .mmp-renewal-control,
  .mmp-cancel-confirm {
    margin-top: 16px;
    border: 1px solid #e2e8f0;
    border-radius: 22px;
    background: #fff;
    padding: 18px;
  }

  .mmp-renewal-control {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
  }

  .mmp-renewal-control > div {
    min-width: 0;
  }

  .mmp-renewal-control strong,
  .mmp-cancel-confirm strong {
    display: block;
    margin-top: 5px;
    color: #0f172a;
    font-size: 18px;
    font-weight: 950;
  }

  .mmp-renewal-control p,
  .mmp-cancel-confirm p {
    margin: 6px 0 0;
    color: #64748b;
    font-size: 13px;
    line-height: 1.45;
    font-weight: 760;
  }

  .mmp-renewal-control.is-resume {
    background: #f0fdfa;
    border-color: rgba(0,103,95,.16);
    color: #00675f;
  }

  .mmp-renewal-control.is-closed {
    background: #f8fafc;
    color: #64748b;
  }

  .mmp-cancel-renewal-btn,
  .mmp-cancel-confirm-btn,
  .mmp-resume-btn {
    min-height: 44px;
    border-radius: 999px;
    padding: 0 16px;
    font-size: 12.5px;
    font-weight: 950;
    cursor: pointer;
    flex: 0 0 auto;
  }

  .mmp-cancel-renewal-btn,
  .mmp-cancel-confirm-btn {
    border: 1px solid #fecaca;
    background: #fff;
    color: #b91c1c;
  }

  .mmp-cancel-confirm-btn {
    background: #b91c1c;
    color: #fff;
    border-color: #b91c1c;
  }

  .mmp-resume-btn {
    border: 0;
    background: #00675f;
    color: #fff;
  }

  .mmp-cancel-renewal-btn:disabled,
  .mmp-cancel-confirm-btn:disabled,
  .mmp-resume-btn:disabled,
  .mmp-embedded-toolbar button:disabled {
    opacity: .55;
    cursor: not-allowed;
  }

  .mmp-confirm-actions {
    margin-top: 16px;
    display: grid;
    grid-template-columns: minmax(120px, .7fr) minmax(190px, 1.3fr);
    gap: 10px;
  }

  .mmp-page-embedded {
    min-height: auto !important;
    background: transparent !important;
    padding: 0 !important;
    max-width: none !important;
    margin: 0 !important;
  }

  .mmp-page-embedded .mmp-head,
  .mmp-page-embedded .mmp-hero { display: none !important; }

  @media (max-width: 760px) {
    .mmp-page { padding: 10px 0 0; }
    .mmp-hero { align-items: flex-start; flex-direction: column; padding: 16px; border-radius: 22px; }
    .mmp-title h1 { font-size: 26px; }
    .mmp-title p { font-size: 12.5px; }
    .mmp-summary { justify-content: flex-start; }
    .mmp-list { gap: 12px; }
    .mmp-card {
      border-radius: 22px;
      padding: 14px;
      box-shadow: 0 12px 28px rgba(15,23,42,.055);
    }
    .mmp-card-top { align-items: center; gap: 10px; }
    .mmp-badge { width: 44px; height: 44px; border-radius: 16px; }
    .mmp-card-title strong { font-size: 18px; line-height: 1.05; }
    .mmp-card-title span { font-size: 11.5px; }
    .mmp-status {
      margin-left: auto;
      padding: 7px 10px;
      font-size: 11.5px;
    }
    .mmp-card p {
      margin-top: 10px;
      font-size: 12.5px;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    .mmp-benefits { margin-top: 10px; gap: 6px; }
    .mmp-benefits span {
      padding: 7px 9px;
      font-size: 11.5px;
    }
    .mmp-meta-strip { display: none; }
    .mmp-usage-box {
      margin-top: 10px;
      border-radius: 18px;
      padding: 10px;
    }
    .mmp-usage-head { margin-bottom: 8px; }
    .mmp-usage-head strong { font-size: 12.5px; }
    .mmp-usage-head span { font-size: 11.5px; }
    .mmp-unlimited-inline {
      min-height: 0;
      border-radius: 16px;
      padding: 12px;
      gap: 10px;
    }
    .mmp-unlimited-inline strong { font-size: 14.5px; }
    .mmp-unlimited-inline small {
      font-size: 11.5px;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    .mmp-credit-hero { gap: 8px; margin-bottom: 10px; }
    .mmp-credit-hero > div { border-radius: 15px; padding: 10px; }
    .mmp-credit-hero strong { font-size: 23px; }
    .mmp-progress { height: 9px; }
    .mmp-vigency-row { display: none; }
    .mmp-card-actions {
      grid-template-columns: minmax(0,.78fr) minmax(0,1.22fr);
      gap: 8px;
      margin-top: 11px;
    }
    .mmp-secondary-btn,
    .mmp-book-btn {
      min-height: 42px;
      padding: 0 12px;
      font-size: 12.5px;
      border-radius: 15px;
    }
    .mmp-book-label-full { display: none; }
    .mmp-book-label-mobile { display: inline; }
    .mmp-detail-usage-grid,
    .mmp-detail-grid { grid-template-columns: 1fr; }
    .mmp-modal-layer {
      align-items: flex-end;
      padding: 0;
    }
    .mmp-modal-card {
      width: 100%;
      max-height: 88dvh;
      border-radius: 28px 28px 0 0;
      padding: 18px;
      box-shadow: 0 -24px 70px rgba(15,23,42,.24);
    }
    .mmp-detail-head { align-items: flex-start; padding-right: 44px; }
    .mmp-badge.is-large { width: 58px; height: 58px; border-radius: 20px; }
    .mmp-detail-title h2 { font-size: 23px; }
    .mmp-detail-section { border-radius: 20px; padding: 13px; }
    .mmp-empty.is-polished { align-items: flex-start; flex-direction: column; }
  }

  @media (max-width: 420px) {
    .mmp-card { padding: 13px; border-radius: 20px; }
    .mmp-card-actions { grid-template-columns: 1fr 1.15fr; }
    .mmp-secondary-btn,
    .mmp-book-btn { min-height: 40px; font-size: 12px; }
    .mmp-benefits span:nth-child(n+3) { display: none; }
  }

  /* =========================================================
     LOOP CLIENT MEMBERSHIP FINAL POLISH V71_2
     Consumer-first management modal + compact membership card.
     Logic/callables intentionally unchanged.
     ========================================================= */

  .mmp-manage-grid.is-compact {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .mmp-manage-grid.is-compact .mmp-info-tile {
    min-height: 78px;
    padding: 12px 14px;
  }

  .mmp-manage-modal .mmp-detail-head {
    margin-bottom: 12px;
  }

  .mmp-manage-modal .mmp-action-message {
    margin-top: 10px;
    margin-bottom: 0;
  }

  .mmp-manage-modal .mmp-renewal-control,
  .mmp-manage-modal .mmp-cancel-confirm {
    margin-top: 12px;
    padding: 15px 16px;
    border-radius: 18px;
  }

  .mmp-manage-modal .mmp-renewal-control > div,
  .mmp-manage-modal .mmp-cancel-confirm > div:first-child {
    gap: 3px;
  }

  .mmp-manage-modal .mmp-renewal-control strong,
  .mmp-manage-modal .mmp-cancel-confirm strong {
    line-height: 1.25;
  }

  .mmp-manage-modal .mmp-renewal-control p,
  .mmp-manage-modal .mmp-cancel-confirm p {
    max-width: 560px;
    margin-top: 4px;
    line-height: 1.45;
  }

  .mmp-manage-modal .mmp-modal-footnote {
    margin-top: 10px;
  }

  .mmp-page-management .mmp-card {
    padding-bottom: 14px;
  }

  .mmp-page-management .mmp-vigency-row {
    margin-bottom: 10px;
  }

  .mmp-page-management .mmp-card-actions {
    margin-top: 0;
  }

  @media (max-width: 760px) {
    .mmp-manage-grid.is-compact {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .mmp-manage-modal .mmp-renewal-control,
    .mmp-manage-modal .mmp-cancel-confirm {
      padding: 14px;
    }
  }

  @media (max-width: 520px) {
    .mmp-manage-grid.is-compact {
      grid-template-columns: 1fr;
      gap: 8px;
    }

    .mmp-manage-grid.is-compact .mmp-info-tile {
      min-height: 68px;
    }
  }

  /* LOOP BENEFITS MEMBERSHIP CARD REVAMP V74.3 */
  .mmp-meta-strip.is-simple-benefit {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    margin-top: 10px;
  }

  .mmp-meta-strip.is-credits-benefit {
    gap: 8px;
    margin-top: 10px;
  }

  .mmp-meta-strip.is-simple-benefit .mmp-info-tile,
  .mmp-meta-strip.is-credits-benefit .mmp-info-tile {
    min-height: 64px;
    padding: 10px 12px;
  }

  .mmp-benefit-summary {
    margin-top: 10px;
    padding: 12px 14px;
    border: 1px solid #cde8e3;
    border-radius: 17px;
    background: linear-gradient(135deg, rgba(235,250,247,.92), rgba(247,252,251,.96));
    display: grid;
    grid-template-columns: 40px minmax(0, 1fr);
    align-items: center;
    gap: 11px;
  }

  .mmp-benefit-summary.is-discount {
    border-color: #e4decf;
    background: linear-gradient(135deg, rgba(250,248,241,.96), rgba(255,253,248,.98));
  }

  .mmp-benefit-summary-icon {
    width: 40px;
    height: 40px;
    border-radius: 13px;
    display: grid;
    place-items: center;
    background: #dcf6f1;
    color: #007d72;
  }

  .mmp-benefit-summary.is-discount .mmp-benefit-summary-icon {
    background: #f4eee0;
    color: #896725;
  }

  .mmp-benefit-summary > div {
    min-width: 0;
    display: grid;
    gap: 2px;
  }

  .mmp-benefit-summary > div > span {
    color: #728095;
    font-size: 9px;
    font-weight: 900;
    letter-spacing: .08em;
    text-transform: uppercase;
  }

  .mmp-benefit-summary strong {
    color: #13263a;
    font-size: 14px;
    line-height: 1.2;
  }

  .mmp-benefit-summary small {
    color: #65768a;
    font-size: 10px;
    line-height: 1.4;
    font-weight: 650;
  }

  .mmp-usage-box.is-credits {
    margin-top: 10px;
    padding: 12px 14px;
    border-radius: 17px;
  }

  .mmp-usage-box.is-credits .mmp-usage-head {
    margin-bottom: 9px;
  }

  .mmp-vigency-row {
    margin-top: 11px;
  }

  .mmp-card-actions {
    margin-top: 10px;
  }

  @media (max-width: 620px) {
    .mmp-meta-strip.is-simple-benefit {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .mmp-benefit-summary {
      padding: 11px 12px;
      grid-template-columns: 38px minmax(0, 1fr);
      gap: 9px;
      border-radius: 15px;
    }

    .mmp-benefit-summary-icon {
      width: 38px;
      height: 38px;
      border-radius: 12px;
    }
  }

  @media (max-width: 420px) {
    .mmp-meta-strip.is-simple-benefit {
      grid-template-columns: 1fr 1fr;
      gap: 6px;
    }

    .mmp-meta-strip.is-simple-benefit .mmp-info-tile {
      min-height: 58px;
      padding: 9px 10px;
    }

    .mmp-benefit-summary small {
      font-size: 9.5px;
    }
  }
/* Keep close controls above positioned detail headers on Safari and desktop. */
.mmp-modal-card .mmp-modal-close{z-index:20!important;pointer-events:auto!important;min-width:44px;min-height:44px;touch-action:manipulation}
.mmp-modal-card .mmp-modal-close svg{pointer-events:none}
.mmp-modal-card .mmp-detail-head{padding-right:54px!important;min-width:0}
.mmp-modal-card .mmp-detail-title{min-width:0;overflow-wrap:anywhere}
`;
